// Anticipos registrados por la clínica — lógica de pantalla sin React (CL.4 A2, 2 Oct 2026).
//
// Contrato del backend (CL.4 A1, `backend/server_pg.py` + `backend/anticipos.py`):
//   · La clínica REGISTRA un anticipo que ya recibió. No se cobra nada en línea.
//   · Registrar un anticipo ligado a una cita `Pendiente` la pasa a `Confirmado` (lo hace el
//     backend, en la misma transacción). La pantalla sólo vuelve a leer la Agenda.
//   · El límite es la VÍSPERA de la cita y se DERIVA. Registrar tarde se permite: la
//     respuesta trae `fuera_de_plazo: true` y la pantalla lo avisa. Nada se cancela solo.
//   · Estados de la cita respecto del anticipo (derivados, nunca guardados):
//     no_requiere | pendiente | vencido | recibido | aplicado | retenido | devuelto.
//
// 🔴 LOS IMPORTES SON CADENAS, igual que en todo el módulo de cobros (lib/money.js): nunca
//    pasan por `Number`/`parseFloat` para operar. Se validan y se normalizan en CENTAVOS
//    ENTEROS y viajan al backend como "1500.50".
//
// 🔴 TODOS LOS TEXTOS VISIBLES NUEVOS VIVEN EN `TEXTOS` Y LLEVAN LA MARCA
//    `COPY PENDIENTE DE JESÚS`: son copy afirmativo nuevo de cara a la clínica y pasan por
//    la parada de copy antes de publicarse. Las etiquetas de formas de pago NO son nuevas:
//    son las mismas que ya mostraba el modal de cobro.

import { aCentavos, deCentavos, formatoDinero } from "./money";

// Formas de pago del módulo de cobros (c_FormaPago del SAT). UNA sola lista: la usan el
// modal de cobro y los formularios de anticipo. Para un anticipo están PROHIBIDAS la vacía,
// la `30` (aplicación de anticipos) y la `99` (por definir) — el backend y el CHECK de la
// tabla lo imponen; aquí simplemente no se ofrecen.
export const FORMAS_PAGO = [
  { valor: "01", etiqueta: "Efectivo" },
  { valor: "03", etiqueta: "Transferencia" },
  { valor: "04", etiqueta: "Tarjeta de crédito" },
  { valor: "28", etiqueta: "Tarjeta de débito" },
];

export const FORMAS_PROHIBIDAS_ANTICIPO = ["", "30", "99"];

// Cuántos días hacia atrás se puede fechar un anticipo. Es una AYUDA de la pantalla (el
// `min` del campo); quien decide es el backend (`anticipos.ANTICIPO_DIAS_ATRAS`), con la
// fecha de negocio de la clínica, y su rechazo se muestra tal cual.
export const DIAS_ATRAS_AYUDA = 7;

/* eslint-disable no-template-curly-in-string */
export const TEXTOS = {
  // ── Indicador en Agenda / Ocupación ────────────────────────────────────────
  estado_pendiente: "Anticipo pendiente", // Copy aprobado por Jesús (2 Oct 2026)
  estado_vencido: "Anticipo vencido", // Copy aprobado por Jesús (2 Oct 2026)
  estado_recibido: "Anticipo recibido", // Copy aprobado por Jesús (2 Oct 2026)
  estado_aplicado: "Anticipo aplicado", // Copy aprobado por Jesús (2 Oct 2026)
  estado_retenido: "Anticipo retenido", // Copy aprobado por Jesús (2 Oct 2026)
  estado_devuelto: "Anticipo devuelto", // Copy aprobado por Jesús (2 Oct 2026)
  // Corrección 1: el indicador de la Agenda pinta una palabra corta junto al ícono (a 390 px
  // la etiqueta larga quedaba en «Antic…»). La larga va en el `title` y en la ventana.
  corto_pendiente: "Sin pago", // Copy aprobado por Jesús (2 Oct 2026)
  corto_vencido: "Vencido", // Copy aprobado por Jesús (2 Oct 2026)
  corto_recibido: "Recibido", // Copy aprobado por Jesús (2 Oct 2026)
  corto_aplicado: "Aplicado", // Copy aprobado por Jesús (2 Oct 2026)
  corto_retenido: "Retenido", // Copy aprobado por Jesús (2 Oct 2026)
  corto_devuelto: "Devuelto", // Copy aprobado por Jesús (2 Oct 2026)

  // ── Ventana del anticipo de una cita ───────────────────────────────────────
  titulo_modal: "Anticipo de la cita", // Copy aprobado por Jesús (2 Oct 2026)
  cargando: "Cargando anticipo…", // Copy aprobado por Jesús (2 Oct 2026)
  limite: "Fecha límite para recibirlo:", // Copy aprobado por Jesús (2 Oct 2026)
  sugerido: "Monto sugerido del servicio:", // Copy aprobado por Jesús (2 Oct 2026)
  sin_registros: "Esta cita no tiene anticipos registrados.", // Copy aprobado por Jesús (2 Oct 2026)
  registrados: "Anticipos registrados", // Copy aprobado por Jesús (2 Oct 2026)
  registrar_titulo: "Registrar anticipo recibido", // Copy aprobado por Jesús (2 Oct 2026)
  registrar_ayuda: "Registra un anticipo que la clínica ya recibió.", // Copy aprobado por Jesús (2 Oct 2026)
  // Sólo si la cita está Pendiente: es el único caso en que el backend la confirma.
  registrar_confirma: "Al registrarlo, la cita pasa a Confirmado.", // Copy aprobado por Jesús (2 Oct 2026)
  campo_monto: "Monto", // Copy aprobado por Jesús (2 Oct 2026)
  campo_forma: "Forma de pago", // Copy aprobado por Jesús (2 Oct 2026)
  campo_forma_elige: "Elige una", // Copy aprobado por Jesús (2 Oct 2026)
  campo_fecha: "Fecha en que se recibió", // Copy aprobado por Jesús (2 Oct 2026)
  campo_referencia: "Referencia", // Copy aprobado por Jesús (2 Oct 2026)
  campo_referencia_ph: "Folio de la transferencia, número de recibo…", // Copy aprobado por Jesús (2 Oct 2026)
  campo_nota: "Nota", // Copy aprobado por Jesús (2 Oct 2026)
  boton_registrar: "Registrar anticipo", // Copy aprobado por Jesús (2 Oct 2026)
  registrando: "Registrando…", // Copy aprobado por Jesús (2 Oct 2026)
  registrado_ok: "Anticipo registrado", // Copy aprobado por Jesús (2 Oct 2026)
  fuera_de_plazo: "Se registró después de la fecha límite (la víspera de la cita). Queda registrado igual.", // Copy aprobado por Jesús (2 Oct 2026)
  error_monto: "Escribe un monto mayor que cero, con máximo dos decimales.", // Copy aprobado por Jesús (2 Oct 2026)
  error_forma: "Indica con qué forma de pago se recibió.", // Copy aprobado por Jesús (2 Oct 2026)
  cancelada_sin_anticipo: "Esta cita está cancelada: no admite anticipo.", // Copy aprobado por Jesús (2 Oct 2026)
  anular_campo: "Anular el anticipo (motivo obligatorio)", // Copy aprobado por Jesús (2 Oct 2026)
  anular_ph: "Error de captura, monto equivocado…", // Copy aprobado por Jesús (2 Oct 2026)
  anular_boton: "Anular", // Copy aprobado por Jesús (2 Oct 2026)
  anulando: "Anulando…", // Copy aprobado por Jesús (2 Oct 2026)
  anulado_ok: "Anticipo anulado", // Copy aprobado por Jesús (2 Oct 2026)
  anulado_cita_revertida: "Anticipo anulado. La cita volvió a Pendiente.", // Copy aprobado por Jesús (2 Oct 2026)
  anulado_marca: "anulado:", // Copy aprobado por Jesús (2 Oct 2026)
  resolver_boton: "Decidir qué pasa con el anticipo", // Copy aprobado por Jesús (2 Oct 2026)
  cerrar: "Cerrar", // Copy aprobado por Jesús (2 Oct 2026)
  recibido_el: "recibido el", // Copy aprobado por Jesús (2 Oct 2026)
  devuelto_el: "devuelto el", // Copy aprobado por Jesús (2 Oct 2026)

  // ── Cobro ──────────────────────────────────────────────────────────────────
  aplicar_label: "Aplicar anticipo de ${monto} recibido el ${fecha}", // Copy aprobado por Jesús (2 Oct 2026)
  aplicar_resta: "Resta por pagar después del anticipo", // Copy aprobado por Jesús (2 Oct 2026)
  aplicar_error_lectura: "No se pudo leer el anticipo de esta cita; si cobras ahora no se aplicará.", // Copy aprobado por Jesús (2 Oct 2026)

  // ── Diálogo al cancelar / no asistió ───────────────────────────────────────
  desenlace_titulo: "¿Qué pasa con el anticipo?", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_intro: "Esta cita tiene un anticipo de ${monto} recibido el ${fecha} que no se ha aplicado.", // Copy aprobado por Jesús (2 Oct 2026)
  // Un solo criterio, el del backend: el cobro de retención va a nombre del médico del
  // anticipo (el de la cita); sin médico, es de la clínica.
  desenlace_retener: "Se queda como ingreso (no se devuelve)", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_retener_ayuda: "Cuenta como ingreso del médico de la cita, con la fecha de hoy.", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_devolver: "Se devolvió al paciente", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_devolver_ayuda: "No es ingreso. Indica cuándo y cómo se devolvió.", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_fecha: "Fecha de la devolución", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_forma: "Forma de devolución", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_posponer: "Decidir después", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_guardar: "Guardar", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_guardando: "Guardando…", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_retenido_ok: "El anticipo quedó como ingreso", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_devuelto_ok: "Devolución registrada", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_elige: "Elige una opción.", // Copy aprobado por Jesús (2 Oct 2026)
  desenlace_sin_vivo: "Esta cita ya no tiene un anticipo por aplicar.", // Copy aprobado por Jesús (2 Oct 2026)

  // ── Servicios (Doctores) ───────────────────────────────────────────────────
  servicio_requiere: "Requiere anticipo", // Copy aprobado por Jesús (2 Oct 2026)
  servicio_monto: "Anticipo sugerido (MXN)", // Copy aprobado por Jesús (2 Oct 2026)
  servicio_guardar: "Guardar anticipo", // Copy aprobado por Jesús (2 Oct 2026)
  servicio_guardado_ok: "Anticipo del servicio guardado", // Copy aprobado por Jesús (2 Oct 2026)
  servicio_resumen: "Anticipo sugerido ${monto}", // Copy aprobado por Jesús (2 Oct 2026)
  servicio_error_monto: "Si el servicio requiere anticipo, escribe el monto sugerido (mayor que cero, máximo dos decimales).", // Copy aprobado por Jesús (2 Oct 2026)

  // ── Reportes ───────────────────────────────────────────────────────────────
  rep_pestana: "Anticipos", // Copy aprobado por Jesús (2 Oct 2026)
  rep_criterio: "Por fecha de recepción del anticipo; aplicados y retenidos, por la fecha en que se aplicaron o retuvieron. El saldo por aplicar es al último día del periodo. Los anticipos anulados no suman. Un anticipo no aparece en «Entró en el periodo» de Ingresos: se ve aquí.", // Copy aprobado por Jesús (2 Oct 2026)
  rep_recibidos: "Recibidos", // Copy aprobado por Jesús (2 Oct 2026)
  rep_recibidos_ayuda: "Anticipos que entraron en el periodo", // Copy aprobado por Jesús (2 Oct 2026)
  rep_aplicados: "Aplicados", // Copy aprobado por Jesús (2 Oct 2026)
  rep_aplicados_ayuda: "Usados para pagar un cobro en el periodo", // Copy aprobado por Jesús (2 Oct 2026)
  rep_retenidos: "Retenidos", // Copy aprobado por Jesús (2 Oct 2026)
  rep_retenidos_ayuda: "Se quedaron como ingreso (no reembolsables)", // Copy aprobado por Jesús (2 Oct 2026)
  rep_devueltos: "Devueltos", // Copy aprobado por Jesús (2 Oct 2026)
  rep_devueltos_ayuda: "Devueltos al paciente en el periodo", // Copy aprobado por Jesús (2 Oct 2026)
  rep_saldo: "Saldo por aplicar al ${corte}", // Copy aprobado por Jesús (2 Oct 2026)
  rep_saldo_ayuda: "Recibidos y todavía sin aplicar, retener ni devolver", // Copy aprobado por Jesús (2 Oct 2026)
  rep_tabla_por_aplicar: "Por aplicar al ${corte}", // Copy aprobado por Jesús (2 Oct 2026)
  rep_tabla_recibidos: "Recibidos en el periodo", // Copy aprobado por Jesús (2 Oct 2026)
  rep_col_fecha: "Recibido", // Copy aprobado por Jesús (2 Oct 2026)
  rep_col_paciente: "Paciente", // Copy aprobado por Jesús (2 Oct 2026)
  rep_col_medico: "Médico", // Copy aprobado por Jesús (2 Oct 2026)
  rep_col_monto: "Monto", // Copy aprobado por Jesús (2 Oct 2026)
  rep_col_forma: "Forma de pago", // Copy aprobado por Jesús (2 Oct 2026)
  rep_col_referencia: "Referencia", // Copy aprobado por Jesús (2 Oct 2026)
  rep_col_cita: "Fecha de la cita", // Copy aprobado por Jesús (2 Oct 2026)
  rep_col_estado: "Estado", // Copy aprobado por Jesús (2 Oct 2026)
  rep_vacio_por_aplicar: "No hay anticipos por aplicar a esa fecha.", // Copy aprobado por Jesús (2 Oct 2026)
  rep_vacio_recibidos: "No se recibieron anticipos en el periodo.", // Copy aprobado por Jesús (2 Oct 2026)
  rep_anulados: "Además hay ${n} ${cosa} por ${importe}. No suman.", // Copy aprobado por Jesús (2 Oct 2026)
  rep_anulado_uno: "anticipo anulado", // Copy aprobado por Jesús (2 Oct 2026)
  rep_anulado_varios: "anticipos anulados", // Copy aprobado por Jesús (2 Oct 2026)
};
/* eslint-enable no-template-curly-in-string */

/** Sustituye `${clave}` en un texto de `TEXTOS`. */
export function texto(clave, vars = {}) {
  const base = TEXTOS[clave] ?? clave;
  return base.replace(/\$\{(\w+)\}/g, (_, k) => (vars[k] === undefined ? "" : String(vars[k])));
}

// ── Indicador ─────────────────────────────────────────────────────────────────

const ESTILOS = {
  pendiente: "bg-amber-50 text-amber-800 border-amber-200",
  vencido: "bg-red-50 text-red-700 border-red-200",
  recibido: "bg-emerald-50 text-emerald-700 border-emerald-200",
  aplicado: "bg-slate-100 text-slate-600 border-slate-200",
  retenido: "bg-slate-100 text-slate-600 border-slate-200",
  devuelto: "bg-slate-100 text-slate-600 border-slate-200",
};

/**
 * Qué indicador pintar para una cita. `null` = no se pinta nada.
 *
 * · `no_requiere` (o ausente: el backend sin la migración no manda el campo) → nada.
 * · Una cita `Cancelado` no muestra `pendiente`/`vencido`: ya no hay nada que pedirle al
 *   paciente, y el backend la deriva `vencido` por la regla literal (A1, decisión 12). Sí
 *   muestra recibido/aplicado/retenido/devuelto, que son dinero real.
 */
export function indicadorAnticipo(estado, statusCita) {
  if (!estado || !ESTILOS[estado]) return null;
  if (statusCita === "Cancelado" && (estado === "pendiente" || estado === "vencido")) return null;
  return { estado, etiqueta: TEXTOS[`estado_${estado}`], corta: TEXTOS[`corto_${estado}`], clase: ESTILOS[estado] };
}

// Estado del ANTICIPO (vista `anticipos_estado`) → estado de la CITA, para pintarlo con la
// misma palabra en todas las pantallas («Anticipo recibido» = por aplicar).
export const ESTADO_CITA_DE_ANTICIPO = {
  por_aplicar: "recibido", aplicado: "aplicado", retenido: "retenido", devuelto: "devuelto",
};

// Estados de la cita en los que la UI ofrece REGISTRAR un anticipo. Medido en el backend
// (`registrar_anticipo`): rechaza sólo `Cancelado`, pero confirma sólo desde `Pendiente`.
// La UI se limita a las citas por venir (Pendiente / Confirmado); en Atendido, No asistió o
// Cancelado la ventana es de lectura o de desenlace.
export const STATUS_QUE_ADMITEN_REGISTRO = ["Pendiente", "Confirmado"];

/**
 * ¿Se ofrece el formulario de registro? Sólo con la cita Pendiente / Confirmado y SIN un
 * anticipo vivo. «Vivo» es lo que mide el backend para `ANTICIPO_YA_REGISTRADO`:
 * `anulado_at IS NULL AND resolucion IS NULL`, o sea por aplicar **o aplicado**.
 */
export function puedeRegistrarEnCita(statusCita, lista) {
  if (!STATUS_QUE_ADMITEN_REGISTRO.includes(statusCita)) return false;
  const items = (lista && lista.items) || [];
  return !items.some((i) => i.estado === "por_aplicar" || i.estado === "aplicado");
}

// ── Montos ────────────────────────────────────────────────────────────────────

/** "1500.5" → "1500.50"; null si no es un importe válido mayor que cero (máx. 2 decimales). */
export function montoCanonico(valor) {
  const c = aCentavos(valor);
  if (c === null || c <= 0) return null;
  return deCentavos(c);
}

/** "2026-10-01" → "01/10/2026". Sin `Date`: una fecha de negocio no tiene zona horaria. */
export function fechaCorta(iso) {
  if (!iso || typeof iso !== "string") return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Hoy y el mínimo sugerido, en la fecha LOCAL del navegador (sólo para pintar el campo). */
export function rangoFechaSugerido(ahora = new Date()) {
  const min = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() - DIAS_ATRAS_AYUDA);
  return { hoy: isoLocal(ahora), min: isoLocal(min) };
}

/**
 * Cuerpo de `POST /api/anticipos`. Devuelve `{cuerpo}` o `{error}` (clave de `TEXTOS`).
 *
 * 🔴 `fecha_recibido` sólo viaja si la persona la CAMBIÓ: si no, la pone el backend con la
 *    fecha de negocio de la clínica. Mandar el «hoy» del navegador podría ser «mañana» para
 *    la clínica (otra zona horaria) y daría un rechazo que nadie entendería.
 */
export function cuerpoRegistro({ appointmentId, monto, forma, fecha, fechaTocada, referencia, nota }) {
  const m = montoCanonico(monto);
  if (!m) return { error: "error_monto" };
  const f = (forma || "").trim();
  if (FORMAS_PROHIBIDAS_ANTICIPO.includes(f)) return { error: "error_forma" };
  const cuerpo = {
    appointment_id: appointmentId,
    monto: m,
    forma_pago: f,
    referencia: (referencia || "").trim(),
    nota: (nota || "").trim(),
  };
  if (fechaTocada && fecha) cuerpo.fecha_recibido = fecha;
  return { cuerpo };
}

/**
 * Campos de anticipo para `POST/PUT /api/doctor-services`. `{campos}` o `{error}`.
 * Sin anticipo: `requiere_anticipo: false` y `anticipo_sugerido: null` (no lo toca).
 */
export function camposAnticipoServicio(requiere, sugerido) {
  if (!requiere) return { campos: { requiere_anticipo: false, anticipo_sugerido: null } };
  const m = montoCanonico(sugerido);
  if (!m) return { error: "servicio_error_monto" };
  return { campos: { requiere_anticipo: true, anticipo_sugerido: m } };
}

/** El anticipo «por aplicar» (vivo) de la respuesta de `GET /api/anticipos`, o null. */
export function anticipoPorAplicar(lista) {
  const items = (lista && lista.items) || [];
  return items.find((i) => i.estado === "por_aplicar") || null;
}

/** Al pasar a Cancelado / No asistió con un anticipo recibido sin aplicar, hay que decidir. */
export function pideDesenlace(cita, nuevoStatus) {
  return (
    (nuevoStatus === "Cancelado" || nuevoStatus === "No asistió") &&
    !!cita &&
    cita.anticipo_estado === "recibido"
  );
}

/**
 * Pago inicial de un cobro cuando además se aplica un anticipo, en CENTAVOS ENTEROS.
 * El backend aplica el anticipo ANTES del pago inicial: si el pago inicial fuera el total,
 * el cobro quedaría sobrepagado. Devuelve la cadena a mandar, o null si no queda nada.
 */
export function pagoInicialConAnticipo(totalCentavos, montoAnticipo) {
  const a = aCentavos(montoAnticipo);
  if (totalCentavos === null || a === null) return null;
  const resto = totalCentavos - a;
  return resto > 0 ? deCentavos(resto) : null;
}

export function etiquetaForma(valor) {
  const f = FORMAS_PAGO.find((x) => x.valor === valor);
  return f ? f.etiqueta : valor || "—";
}

export function etiquetaAplicar(anticipo) {
  return texto("aplicar_label", {
    monto: formatoDinero(anticipo.monto),
    fecha: fechaCorta(anticipo.fecha_recibido),
  });
}
